/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react'
import { App } from 'antd'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'

export type UserData = {
  id?: string
  name_first?: string
  name_last?: string
  email?: string
  phone_number?: string
  phone_number_country?: string
  role_type_id?: number
  role_type?: number
  is_invited?: boolean
  is_accepted?: boolean
  invitation_id?: string
}

type CustomError = {
  name_first: string | null
  name_last: string | null
  role_type_id: string | null
  email: string | null
  phone_number: string | null
  message: string | null
}

type UserState = {
  id: string | null
  name_first: string
  name_last: string
  role_type_id: number | null
  email: string
  phone_number: string
  phone_number_country: string
  isLoading: boolean
  error: string | null
  fieldErrors: string[]
  customError: CustomError
  hasInitialEmail: boolean
}

type UserAction =
  | { type: 'SET_FIELD'; field: keyof UserState; value: unknown }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_FIELD_ERRORS'; errors: string[] }
  | { type: 'SET_CUSTOM_ERROR_BY_FIELD'; field: string; message: string | null }
  | { type: 'CLEAR_CUSTOM_ERROR_BY_FIELD'; field: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'RESET_FORM' }
  | { type: 'INIT_EDIT_MODE'; data: UserData }

const initialCustomError: CustomError = {
  name_first: null,
  name_last: null,
  role_type_id: null,
  email: null,
  phone_number: null,
  message: null,
}

const initialState: UserState = {
  id: null,
  name_first: '',
  name_last: '',
  role_type_id: null,
  email: '',
  phone_number: '',
  phone_number_country: 'US',
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: initialCustomError,
  hasInitialEmail: false,
}

function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_FIELD_ERRORS':
      return { ...state, fieldErrors: action.errors }

    case 'SET_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: action.message,
        },
        fieldErrors: action.message
          ? [...new Set([...state.fieldErrors, action.field])]
          : state.fieldErrors,
      }

    case 'CLEAR_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: null,
        },
        fieldErrors: state.fieldErrors.filter((e) => e !== action.field),
      }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        error: null,
        fieldErrors: [],
        customError: initialCustomError,
      }

    case 'RESET_FORM':
      return { ...initialState }

    case 'INIT_EDIT_MODE':
      return {
        ...state,
        id: action.data.id || null,
        name_first: action.data.name_first || '',
        name_last: action.data.name_last || '',
        role_type_id: action.data.role_type_id || action.data.role_type || null,
        email: action.data.email || '',
        phone_number: action.data.phone_number || '',
        phone_number_country: action.data.phone_number_country || 'US',
        hasInitialEmail: Boolean(action.data.email),
      }

    default:
      return state
  }
}

type UserContextValue = {
  state: UserState
  dispatch: React.Dispatch<UserAction>
  isEdit: boolean
  onSubmit: () => Promise<void>
  onCancel: () => void
  onDelete: () => Promise<void>
  setCustomErrorByField: (field: string, message: string | null) => void
  clearCustomErrorByField: (field: string) => void
  entityType: string
  entityId: string
}

const UserContext = createContext<UserContextValue | null>(null)

export const useUserContext = (): UserContextValue => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserContext must be used within UserProvider')
  }
  return context
}

/**
 * Props for the UserProvider context wrapper.
 * @property children - Nested content rendered inside the provider.
 * @property onClose - Callback to close the modal.
 * @property initialData - Optional initial user data for edit mode.
 * @property entityType - Entity type for permissions API calls.
 * @property entityId - Entity identifier for permissions API calls.
 * @property onSuccess - Callback fired after successful API actions.
 */
interface UserProviderProps {
  children: ReactNode
  onClose: () => void
  initialData?: UserData
  entityType: string
  entityId: string
  onSuccess: () => void
}

/**
 * Provides user modal state, actions, and API handlers.
 */
export const UserProvider: React.FC<UserProviderProps> = ({
  children,
  onClose,
  initialData,
  entityType,
  entityId,
  onSuccess,
}) => {
  const [state, dispatch] = useReducer(userReducer, initialState)
  const { message } = App.useApp()
  const isEdit = Boolean(initialData?.id)

  const setCustomErrorByField = useCallback(
    (field: string, errorMessage: string | null) => {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field,
        message: errorMessage,
      })
    },
    []
  )

  const clearCustomErrorByField = useCallback((field: string) => {
    dispatch({ type: 'CLEAR_CUSTOM_ERROR_BY_FIELD', field })
  }, [])

  const setCustomErrors = useCallback(
    (invalid_fields: Record<string, string | string[]>) => {
      const normalizeError = (error: string | string[]): string => {
        return Array.isArray(error) ? error.join(', ') : error
      }

      Object.entries(invalid_fields).forEach(([field, error]) => {
        setCustomErrorByField(field, normalizeError(error))
      })
    },
    [setCustomErrorByField]
  )

  React.useEffect(() => {
    if (initialData && isEdit) {
      dispatch({
        type: 'INIT_EDIT_MODE',
        data: initialData,
      })
    }
  }, [initialData, isEdit])

  const onSubmit = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', value: true })
    dispatch({ type: 'CLEAR_ALL_ERRORS' })

    const roleTypeId =
      state.role_type_id == null ? null : Number(state.role_type_id)

    if (roleTypeId == null || Number.isNaN(roleTypeId)) {
      setCustomErrorByField('role_type_id', 'Role is required')
      dispatch({ type: 'SET_LOADING', value: false })
      return
    }

    try {
      const payload: Record<string, unknown> = {
        name_first: state.name_first.trim(),
        name_last: state.name_last.trim(),
        role_type_id: roleTypeId,
      }

      if (state.email) {
        payload.email = state.email.trim()
      }
      if (state.phone_number) {
        payload.phone_number = state.phone_number
        payload.phone_number_country = state.phone_number_country || 'US'
      }

      if (isEdit && state.id) {
        let slug = `/${entityType}/${entityId}/permissions/${state.id}`
        if (!initialData?.is_accepted) {
          slug = `/invitations/${initialData?.invitation_id}`
        }

        await req(slug, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
        message.success(
          `${state.name_first} ${state.name_last} updated successfully`
        )
      } else {
        await req(`/${entityType}/${entityId}/permissions`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        message.success(
          `${state.name_first} ${state.name_last} created successfully`
        )
      }

      onSuccess()
      onClose()
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'invalid_fields' in error) {
        setCustomErrors(
          error.invalid_fields as Record<string, string | string[]>
        )
      } else {
        const errorMessage = getErrorMessage(error)
        dispatch({ type: 'SET_ERROR', error: errorMessage })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    state.name_first,
    state.name_last,
    state.role_type_id,
    state.email,
    state.phone_number,
    state.phone_number_country,
    state.id,
    isEdit,
    entityType,
    entityId,
    initialData,
    onSuccess,
    onClose,
    message,
    setCustomErrorByField,
    setCustomErrors,
  ])

  const onDelete = useCallback(async () => {
    if (!isEdit || !state.id) {
      return
    }

    dispatch({ type: 'SET_LOADING', value: true })

    try {
      let slug = `/${entityType}/${entityId}/permissions/${state.id}`
      if (!initialData?.is_accepted) {
        slug = `/invitations/${initialData?.invitation_id}`
      }

      await req(slug, {
        method: 'DELETE',
      })
      message.success(
        `${state.name_first} ${state.name_last} removed successfully`
      )
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error)
      dispatch({ type: 'SET_ERROR', error: errorMessage })
      throw error instanceof Error ? error : new Error(errorMessage)
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    isEdit,
    state.id,
    state.name_first,
    state.name_last,
    entityType,
    entityId,
    initialData,
    message,
    onSuccess,
    onClose,
  ])

  const onCancel = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    onClose()
  }, [onClose])

  const value: UserContextValue = {
    state,
    dispatch,
    isEdit,
    onSubmit,
    onCancel,
    onDelete,
    setCustomErrorByField,
    clearCustomErrorByField,
    entityType,
    entityId,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
